<template>
  <div
    class="kk-range__selection"
    :style="{
      gridArea: `${refinedRange[1] + 2} / ${refinedRange[0] + 2} / ${refinedRange[3] + 3} / ${refinedRange[2] + 3}`,
      ...style,
    }"
  >
    <canvas
      ref="canvas"
      :class="`${
        item.mode !== 'fill'
          ? `kk-range__selection-canvas kk-range__selection-theme${index % 10}`
          : 'kk-range__selection-canvas kk-range__selection-themefill'
      }${border}`"
      :style="{ ...canvasStyle }"
    ></canvas>
    <div v-if="showDots" class="kk-range__selection-topleft" @mousedown="mouseDown($event, 0, 'nwse')"></div>
    <div v-if="showDots" class="kk-range__selection-topright" @mousedown="mouseDown($event, 1, 'nesw')"></div>
    <div v-if="showDots" class="kk-range__selection-bottomleft" @mousedown="mouseDown($event, 2, 'nesw')"></div>
    <div
      v-if="showDots || isMainRange"
      data-test-id="range-selection-button"
      :class="`kk-range__selection-bottomright ${isMainRange ? 'kk-range__selection-crosshair' : ''}`"
      @mousedown="mouseDown($event, 3, 'nwse')"
    ></div>
  </div>
</template>
<script>
import { isCell, isRangeWithinRange } from "../../utils/util";
import { EditModes } from "../../utils/constants";
import DesignerMixin from "./DesignerMixin";
import { rangeLabelToIndex, sortRange, rangeIndexToLabel, splitCellId } from "./cell/Utils";
import deepIs from "deep-is";

const Positions = {
  0: "topleft",
  1: "topright",
  2: "bottomleft",
  3: "bottomright",
};

const themeColors = [
  "rgba(13, 102, 154, 0.2)",
  "rgba(148, 114, 194, 0.25)",
  "rgba(180, 117, 152, 0.20)",
  "rgba(223, 78, 92, 0.25)",
  "rgba(8, 50, 119, 0.20)",
  "rgba(0, 133, 159, 0.20)",
  "rgba(110, 169, 119, 0.20)",
  "rgba(205, 203, 72, 0.20)",
  "rgba(208, 172, 79, 0.20)",
  "rgba(107, 76, 37, 0.20)",
];

export default {
  mixins: [DesignerMixin],
  props: ["index", "options", "getPxFromRowIndex", "getPxFromColumnIndex", "item"],
  data() {
    return {
      dragging: false,
    };
  },
  computed: {
    id() {
      return this.item.id;
    },
    range() {
      return this.item.range;
    },
    hScroll() {
      return this.$store.state.cells.hScroll;
    },
    vScroll() {
      return this.$store.state.cells.vScroll;
    },
    refinedRange() {
      const maxHScroll = this.hScroll.end - this.hScroll.index;
      const maxVScroll = this.vScroll.end - this.vScroll.index;

      return [
        Math.max(0, Math.min(maxHScroll, this.range[0] - this.hScroll.index)),
        Math.max(0, Math.min(maxVScroll, this.range[1] - this.vScroll.index)),
        Math.min(maxHScroll, Math.max(0, this.range[2] - this.hScroll.index)),
        Math.min(maxVScroll, Math.max(0, this.range[3] - this.vScroll.index)),
      ];
    },
    style() {
      return {
        margin: `${this.options.margin ?? 0}px`,
      };
    },
    canvasStyle() {
      return {
        borderRadius: `${this.options.borderRadius ?? 0}px`,
      };
    },
    showDots() {
      return (
        !deepIs(sortRange(this.mainRange.range), sortRange(this.range)) &&
        this.item.mode !== "fill" &&
        this.item.mode !== "range"
      );
    },
    isMainRange() {
      return deepIs(this.mainRange.range, this.range);
    },
    editCell() {
      return this.$store.state.cells.editCell;
    },
    border() {
      const range = this.editCell.range;
      let activeRange = isCell(range) ? `${range}:${range}` : range;

      if (activeRange) activeRange = rangeIndexToLabel(sortRange(rangeLabelToIndex(activeRange)));
      const active = range && activeRange === rangeIndexToLabel(this.range);

      return active && this.item.mode !== "fill" ? " kk-range__selection-active" : "";
    },
    cellId() {
      return this.mergedCellIds[this.activeCellId]
        ? this.mergedCellIds[this.activeCellId].replace("_merged", "")
        : this.activeCellId;
    },
    redrawCanvas() {
      return this.$store.state.cells.redrawCanvas;
    },
  },
  watch: {
    activeCellId(newValue) {
      this.drawCanvas();
    },
    hScroll() {
      this.$nextTick(() => {
        this.drawCanvas();
      });
    },
    vScroll() {
      this.$nextTick(() => {
        this.drawCanvas();
      });
    },
    redrawCanvas() {
      this.drawCanvas();
    },
    item(newValue, oldValue) {
      if (!deepIs(newValue, oldValue))
        this.$nextTick(() => {
          this.drawCanvas();
        });
    },
  },
  mounted() {
    document.addEventListener("mouseup", this.mouseUp);
    this.drawCanvas();
  },
  beforeUnmount() {
    document.removeEventListener("mouseup", this.mouseUp);
  },
  methods: {
    drawCanvas() {
      const canvas = this.$refs.canvas;
      if (!canvas) return;
      const ctx = canvas.getContext("2d");
      canvas.width = canvas.clientWidth;
      canvas.height = canvas.clientHeight;

      if (this.item.mode !== "fill") ctx.fillStyle = themeColors[this.index % 10];
      else ctx.fillStyle = "transparent";
      ctx.fillRect(0, 0, canvas.clientWidth, canvas.clientHeight);
      const activeCellRange = this.getRangeByCellId(this.activeCellId);
      const scrollRange = [this.hScroll.index, this.vScroll.index, this.hScroll.end, this.vScroll.end];

      const visibleSelectedRange = [
        Math.max(scrollRange[0], this.range[0]),
        Math.max(scrollRange[1], this.range[1]),
        Math.min(scrollRange[2], this.range[2]),
        Math.min(scrollRange[3], this.range[3]),
      ];

      if (isRangeWithinRange(activeCellRange, scrollRange)) {
        const [rowId, columnId] = splitCellId(this.cellId);

        const firstCellX = this.getPxFromColumnIndex(visibleSelectedRange[0]);
        const firstCellY = this.getPxFromRowIndex(visibleSelectedRange[1]);
        const { margin, borderRadius } = this.options;
        const x = this.getPxFromColumnIndex(this.columnMap[columnId].index) - firstCellX;
        const y = this.getPxFromRowIndex(this.rowMap[rowId].index) - firstCellY;
        const width = this.getMergedCellWidth(this.cellId) - 2 * margin;
        const height = this.getMergedCellHeight(this.cellId) - 2 * margin;

        // https://stackoverflow.com/questions/51232811/canvas-clearrect-with-rounded-corners
        // kshetline answer
        ctx.save();
        ctx.beginPath();
        ctx.roundRect(x, y, width, height, borderRadius);
        ctx.clip();
        ctx.clearRect(x, y, width, height);
        ctx.restore();
      }
    },
    async mouseDown(e, position, cursorType) {
      this.$store.dispatch("cells/setActiveRangeId", this.id);
      let id = this.id;
      let mode = "";
      if (this.isMainRange) {
        const title = rangeIndexToLabel(this.range);
        const activeRangeId = await this.$store.dispatch("cells/setFormulaRange", { title, isMainRange: false });
        mode = "fill";
        this.$store.dispatch("cells/updateActiveRange", { id: activeRangeId, title, mode });
        id = activeRangeId;
        cursorType = "crosshair";
      }

      let drag = "range";
      if (mode == "fill") {
        this.$store.dispatch("pdesign/setEditMode", EditModes.Disable);
      } else {
        drag = "drag";
      }

      this.$store.dispatch("cells/setRangeDragging", {
        id,
        position: Positions[position],
        cursorType,
        range: this.range,
        ctrlKey: e.ctrlKey,
        drag,
      });
    },
    mouseUp() {
      this.$store.dispatch("cells/setRangeDragging", {
        id: null,
        position: "",
        cursorType: "",
        range: null,
        ctrlKey: false,
      });
    },
  },
};
</script>
<style lang="less" scoped>
@import "../../assets/styles/variables.less";

.kk-range__selection {
  border-radius: 0.25rem;
  z-index: 13;
  pointer-events: none;
  position: relative;
  // margin: 0.2rem;

  &-canvas {
    width: 100%;
    height: 100%;
  }

  &-topleft,
  &-topright,
  &-bottomright,
  &-bottomleft {
    pointer-events: all;
    position: absolute;
    width: 6px;
    height: 6px;
    background-color: currentColor;
    z-index: 2005;
    border: 1.2px solid @kk-color-lighter-gray;
  }
  &-topleft {
    transform: translate(-50%, -50%);
    top: 0;
    left: 0;
    cursor: nwse-resize;
  }
  &-topright {
    transform: translate(50%, -50%);
    top: 0;
    right: 0;
    cursor: nesw-resize;
  }
  &-bottomleft {
    transform: translate(-50%, 50%);
    bottom: 0;
    left: 0;
    cursor: nesw-resize;
  }
  &-bottomright {
    transform: translate(50%, 50%);
    bottom: 0;
    right: 0;
    cursor: nwse-resize;
  }
  &-crosshair {
    cursor: crosshair;
  }

  &-theme0 {
    box-shadow: inset 0 0 0 1px @kk-color-primary-blue;
  }

  &-theme0 &-topleft,
  &-theme0 &-topright,
  &-theme0 &-bottomright,
  &-theme0 &-bottomleft {
    background-color: @kk-color-primary-blue;
  }
  &-theme1 {
    outline: 2px solid rgba(148, 114, 194, 1);
  }
  &-theme1 &-topright,
  &-theme1 &-bottomleft,
  &-theme1 &-topleft,
  &-theme1 &-bottomright {
    background-color: rgba(148, 114, 194, 1);
  }
  &-theme2 {
    outline: 2px solid rgba(180, 117, 152, 1);
  }
  &-theme2 &-topleft,
  &-theme2 &-topright,
  &-theme2 &-bottomright,
  &-theme2 &-bottomleft {
    background-color: rgba(180, 117, 152, 1);
  }
  &-theme3 {
    outline: 2px solid rgba(223, 78, 92, 1);
  }
  &-theme3 &-topleft,
  &-theme3 &-topright,
  &-theme3 &-bottomright,
  &-theme3 &-bottomleft {
    background-color: rgba(223, 78, 92, 1);
  }

  &-theme4 {
    outline: 2px solid rgba(8, 50, 119, 0.9);
  }
  &-theme4 &-topleft,
  &-theme4 &-topright,
  &-theme4 &-bottomright,
  &-theme4 &-bottomleft {
    background-color: rgba(8, 50, 119, 0.9);
  }

  &-theme5 {
    outline: 2px solid rgba(0, 133, 159, 1);
  }
  &-theme5 &-topleft,
  &-theme5 &-topright,
  &-theme5 &-bottomright,
  &-theme5 &-bottomleft {
    background-color: rgba(0, 133, 159, 1);
  }

  &-theme6 {
    outline: 2px solid rgba(110, 169, 119, 1);
  }
  &-theme6 &-topleft,
  &-theme6 &-topright,
  &-theme6 &-bottomright,
  &-theme6 &-bottomleft {
    background-color: rgba(110, 169, 119, 1);
  }

  &-theme7 {
    outline: 2px solid rgba(205, 203, 72, 1);
  }
  &-theme7 &-topleft,
  &-theme7 &-topright,
  &-theme7 &-bottomright,
  &-theme7 &-bottomleft {
    background-color: rgba(205, 203, 72, 1);
  }

  &-theme8 {
    outline: 2px solid rgba(208, 172, 79, 1);
  }
  &-theme8 &-topleft,
  &-theme8 &-topright,
  &-theme8 &-bottomright,
  &-theme8 &-bottomleft {
    background-color: rgba(208, 172, 79, 1);
  }

  &-theme9 {
    outline: 2px solid rgba(107, 76, 37, 1);
  }
  &-theme9 &-topleft,
  &-theme9 &-topright,
  &-theme9 &-bottomright,
  &-theme9 &-bottomleft {
    background-color: rgba(107, 76, 37, 1);
  }

  &-themefill {
    outline: 2px dashed rgb(100, 100, 100);
    background-color: transparent;
  }

  &-active {
    outline: none;
    background-image: linear-gradient(90deg, currentColor 50%, transparent 50%),
      linear-gradient(90deg, currentColor 50%, transparent 50%),
      linear-gradient(0deg, currentColor 50%, transparent 50%), linear-gradient(0deg, currentColor 50%, transparent 50%);
    background-repeat: repeat-x, repeat-x, repeat-y, repeat-y;
    background-size: 15px 2px, 15px 2px, 2px 15px, 2px 15px;
    background-position: left top, right bottom, left bottom, right top;
    animation: border-dance 0.5s infinite linear;
  }
  @keyframes border-dance {
    0% {
      background-position: left top, right bottom, left bottom, right top;
    }
    100% {
      background-position: left 15px top, right 15px bottom, left bottom 15px, right top 15px;
    }
  }
}
</style>
